import { perkpointApi, type ApiPage } from "./client";

/**
 * Fraud detection API (api/src/services/fraud.js).
 *
 * The backend checks every earn and redemption against the program's active
 * fraud rules. A hit raises an alert and applies the rule's action:
 *   flag              let the transaction through, raise an alert
 *   block-earn-burn   refuse this transaction
 *   block-all-future  refuse it and block the member
 * These calls manage the rules and work the resulting alert queue.
 */

export type FraudSeverity = "low" | "medium" | "high" | "critical";
export type FraudAlertType = "velocity" | "threshold" | "pattern" | "blockchain";
export type FraudAlertStatus = "active" | "investigating" | "resolved" | "false_positive";
export type FraudAction = "flag" | "block-earn-burn" | "block-all-future";
export type FraudOutcome = "flagged" | "rejected" | "member_blocked";

export type ApiFraudRule = {
  id: string;
  code: string;
  name: string;
  /** Outlet names. Empty means every outlet. */
  outlets: string[];
  outlet_label: string;
  severity: FraudSeverity;
  status: "active" | "paused";
  enabled: boolean;
  velocity: { transactions: number; days: number; action: FraudAction } | null;
  /** amount is in major units of the program currency. */
  invoice: { amount: number; amount_minor: number; action: FraudAction } | null;
  redemption: { points: number; action: FraudAction } | null;
  currency: string;
  /** Plain-language lines, one per condition. */
  summary: string[];
  triggers: number;
  last_triggered_at: string | null;
  created_at: string;
  updated_at: string;
};

export type FraudRuleInput = {
  name: string;
  outlets: string[];
  severity: FraudSeverity;
  status: "active" | "paused";
  velocity: { transactions: number; days: number; action: FraudAction } | null;
  invoice: { amount: number; action: FraudAction } | null;
  redemption: { points: number; action: FraudAction } | null;
};

export type ApiFraudAlert = {
  id: string;
  code: string;
  member: { id: string; external_member_id: string; name: string; status: string };
  rule: { id: string; code: string; name: string } | null;
  alert_type: FraudAlertType;
  condition: "velocity" | "invoice" | "redemption" | null;
  severity: FraudSeverity;
  risk_score: number;
  status: FraudAlertStatus;
  action: FraudAction;
  outcome: FraudOutcome;
  transaction_kind: "earn" | "burn";
  reference_id: string | null;
  outlet: string | null;
  event_id: string | null;
  ledger_entry_id: string | null;
  txn_code: string | null;
  amount_minor: number | null;
  points: number | null;
  description: string;
  metadata: Record<string, unknown>;
  /** Repeat velocity hits fold into one alert; this counts them. */
  hit_count: number;
  last_hit_at: string;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
  updated_at: string;
};

export type FraudSettings = {
  monitoring_enabled: boolean;
  auto_block_enabled: boolean;
  auto_block_threshold: number;
  updated_at: string | null;
};

export type FraudSummary = {
  currency: string;
  currency_exponent: number;
  alerts: {
    active: number;
    investigating: number;
    high_risk_open: number;
    blocked_last_30_days: number;
    precision_pct: number | null;
  };
  at_risk: { amount_minor: number; points: number };
  open_by_severity: Record<FraudSeverity, number>;
  open_by_type: Record<FraudAlertType, number>;
  rules: { active: number; paused: number; total: number };
  settings: FraudSettings;
};

export type FraudAlertQuery = {
  status?: string;
  severity?: string;
  type?: string;
  search?: string;
  page?: number;
  page_size?: number;
};

export const fraudKeys = {
  all: ["perkpoint", "fraud"] as const,
  program: (programId: string) => ["perkpoint", "fraud", programId] as const,
  summary: (programId: string) => ["perkpoint", "fraud", programId, "summary"] as const,
  alerts: (programId: string, query: FraudAlertQuery) => ["perkpoint", "fraud", programId, "alerts", query] as const,
  rules: (programId: string) => ["perkpoint", "fraud", programId, "rules"] as const,
};

const base = (programId: string) => `/admin/programs/${programId}/fraud`;

export const FraudApi = {
  async summary(programId: string) {
    const { data } = await perkpointApi.get<FraudSummary>(`${base(programId)}/summary`);
    return data;
  },
  async alerts(programId: string, query: FraudAlertQuery = {}) {
    const params = Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== ""));
    const { data } = await perkpointApi.get<ApiPage<ApiFraudAlert>>(`${base(programId)}/alerts`, { params });
    return data;
  },
  async updateAlert(programId: string, alertId: string, status: FraudAlertStatus, note?: string) {
    const { data } = await perkpointApi.patch<ApiFraudAlert>(`${base(programId)}/alerts/${alertId}`, { status, note });
    return data;
  },
  async blockMember(programId: string, alertId: string, reason?: string) {
    const { data } = await perkpointApi.post<{ alert: ApiFraudAlert; resolved_alerts: string[] }>(
      `${base(programId)}/alerts/${alertId}/block-member`,
      { reason },
    );
    return data;
  },
  async rules(programId: string) {
    const { data } = await perkpointApi.get<ApiPage<ApiFraudRule>>(`${base(programId)}/rules`);
    return data;
  },
  async createRule(programId: string, input: FraudRuleInput) {
    const { data } = await perkpointApi.post<ApiFraudRule>(`${base(programId)}/rules`, input);
    return data;
  },
  async updateRule(programId: string, ruleId: string, input: Partial<FraudRuleInput>) {
    const { data } = await perkpointApi.patch<ApiFraudRule>(`${base(programId)}/rules/${ruleId}`, input);
    return data;
  },
  async deleteRule(programId: string, ruleId: string) {
    const { data } = await perkpointApi.delete<{ id: string; code: string; status: "archived" }>(
      `${base(programId)}/rules/${ruleId}`,
    );
    return data;
  },
  async saveSettings(programId: string, input: Partial<Omit<FraudSettings, "updated_at">>) {
    const { data } = await perkpointApi.put<FraudSettings>(`${base(programId)}/settings`, input);
    return data;
  },
};
